"use client";

import { useState, useEffect, useSyncExternalStore } from "react";
import Link from "next/link";
import Image from "next/image";
import { useTheme } from "next-themes";
import { usePathname } from "next/navigation";
import { Button } from "@/components/ui/button";
import { BookingModal } from "@/components/BookingModal";
import { cn } from "@/lib/utils";
import { Menu, X } from "lucide-react";

function useIsMounted() {
  return useSyncExternalStore(
    () => () => {},
    () => true,
    () => false
  );
}

const navLinks = [
  { href: "/#hero", label: "Home" },
  { href: "/#about", label: "About Us" },
  { href: "/services", label: "Services" },
  { href: "/#gallery", label: "Gallery" },
  { href: "/news-and-articles", label: "News & Articles" },
  { href: "/founders", label: "Our Founders" },
  { href: "/careers", label: "Join Our Team" },
  { href: "/#contact", label: "Contact" },
];

export function SiteHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const { resolvedTheme } = useTheme();
  const mounted = useIsMounted();
  const pathname = usePathname();
  const isHome = pathname === "/";

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 20);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  const isLinkActive = (href: string) => {
    if (href === "/services") return pathname.startsWith("/services");
    if (href === "/news-and-articles") return pathname.startsWith("/news-and-articles");
    if (href === "/founders") return pathname === "/founders";
    if (href === "/careers") return pathname.startsWith("/careers");
    return false;
  };

  return (
    <>
      <BookingModal open={bookingOpen} onOpenChange={setBookingOpen} />
      <header
        className={`fixed top-9 right-0 left-0 z-50 transition-all duration-300 ${
          isScrolled || mobileMenuOpen || !isHome
            ? "glass border-border border-b shadow-sm bg-background/95 backdrop-blur"
            : "bg-transparent"
        }`}
      >
        <div className="container mx-auto flex items-center justify-between px-4">
          {/* Logo */}
          <Link
            href="/"
            className="flex cursor-pointer items-center gap-2"
            aria-label="SkinSio Home"
            onClick={() => setMobileMenuOpen(false)}
          >
            <div
              className={cn(
                "flex items-center justify-center rounded-md transition-colors",
                mounted && resolvedTheme === "dark"
                  ? "bg-white shadow-sm"
                  : "bg-transparent"
              )}
            >
              <div className="relative h-20 w-40">
                <Image
                  src="/Logo.PNG"
                  alt="SkinSio Logo"
                  fill
                  sizes="160px"
                  className="object-contain"
                  priority
                  fetchPriority="high"
                  quality={75}
                />
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden items-center gap-6 lg:flex" aria-label="Main Navigation">
            {navLinks.map((link) => {
              const isActive = isLinkActive(link.href);
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  className={cn(
                    "group relative py-2 text-sm font-medium transition-all duration-300",
                    isActive
                      ? "text-golden"
                      : "text-foreground/80 hover:text-golden"
                  )}
                >
                  {link.label}
                  <span
                    className={cn(
                      "bg-golden absolute bottom-0 left-0 h-0.5 transition-all duration-300",
                      isActive ? "w-full" : "w-0 group-hover:w-full"
                    )}
                  />
                </Link>
              );
            })}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-3">
            <Button
              onClick={() => setBookingOpen(true)}
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-3 text-xs sm:px-4 sm:text-sm"
              aria-label="Book an appointment"
            >
              Book Appointment
            </Button>

            <button
              className="lg:hidden p-2 rounded-md text-foreground hover:bg-primary/10 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 top-9 z-40 bg-black/40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Drawer */}
      <div
        className={cn(
          "fixed top-9 right-0 z-40 h-[calc(100%-2.25rem)] w-72 bg-background shadow-2xl transition-transform duration-300 ease-in-out lg:hidden",
          mobileMenuOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        <div className="flex items-center justify-between border-b px-6 py-5">
          <span className="text-lg font-bold text-primary">Menu</span>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="p-1 rounded-md hover:bg-primary/10 transition-colors"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex flex-col px-4 py-6 gap-1" aria-label="Mobile Navigation">
          {navLinks.map((link) => {
            const isActive = isLinkActive(link.href);
            return (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  "flex items-center px-4 py-3 rounded-xl text-base font-medium transition-all duration-200",
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-foreground/80 hover:bg-primary/5 hover:text-primary"
                )}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>

        <div className="px-8 pt-2">
          <Button
            onClick={() => {
              setMobileMenuOpen(false);
              setBookingOpen(true);
            }}
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            Book Appointment
          </Button>
        </div>

        <div className="absolute bottom-8 left-0 right-0 px-8 text-center">
          <p className="text-xs text-muted-foreground mb-2">Call us directly</p>
          <a
            href="tel:+919322022392"
            className="text-primary font-semibold text-sm hover:underline"
            onClick={() => setMobileMenuOpen(false)}
          >
            +91 93220 22392
          </a>
        </div>
      </div>
    </>
  );
}
